// @ts-nocheck - untyped
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import IHubAdapterService from '../../../Services/IHubAdapterService';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import DescriptionIcon from '@mui/icons-material/Description';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import Notification from '../../Layout/Notification';
import { getCustomerDisplayName } from '../../../Services/CustomerMappings';
import { useTheme } from '@mui/material/styles';

import './IHubAdapter.scss';

const getDataTypeIcon = dataType => {
  switch (dataType) {
    case 'IP_SERVICE':
      return <CloudDoneIcon fontSize="large" />;
    case 'COST':
      return <AttachMoneyIcon fontSize="large" />;
    case 'INVOICE':
      return <DescriptionIcon fontSize="large" />;
    case 'REMINDER':
      return <NotificationsActiveIcon fontSize="large" />;
    default:
      return <AssessmentIcon fontSize="large" />;
  }
};

const getDataTypeColor = (dataType, tokens) => {
  switch (dataType) {
    case 'IP_SERVICE':
      return tokens.info;
    case 'COST':
      return tokens.success;
    case 'INVOICE':
      return tokens.warning;
    case 'REMINDER':
      return tokens.danger;
    default:
      return tokens.fgTertiary;
  }
};

const CustomerMigrationHistory = ({ initialCustomer }) => {
  const theme = useTheme();
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const isInvalidRange = startDate && endDate && new Date(endDate) < new Date(startDate);

  const fetchCustomers = useCallback(async () => {
    try {
      setLoadingCustomers(true);
      const response = await IHubAdapterService.getCustomers();
      const raw = response.data;
      const customerList = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.customers)
          ? raw.customers
          : [];
      setCustomers(customerList);

      if (initialCustomer) {
        setSelectedCustomer(initialCustomer);
      } else if (customerList.length > 0) {
        setSelectedCustomer(customerList[0].customerNumber);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
      Notification.showError('Failed to load customers');
    } finally {
      setLoadingCustomers(false);
    }
  }, [initialCustomer]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  useEffect(() => {
    if (initialCustomer) {
      setSelectedCustomer(initialCustomer);
    }
  }, [initialCustomer]);

  const handleSearch = async () => {
    if (!selectedCustomer) {
      Notification.showWarn('Please select a customer');
      return;
    }

    if (isInvalidRange) {
      Notification.showWarn('End date cannot be before start date');
      return;
    }

    try {
      setLoading(true);
      setStatistics(null);

      const formattedStartDate = startDate ? new Date(startDate).toISOString() : null;
      const formattedEndDate = endDate ? new Date(endDate).toISOString() : null;

      const response = await IHubAdapterService.getMigrationHistory(
        selectedCustomer,
        formattedStartDate,
        formattedEndDate
      );

      setStatistics(response.data);
      Notification.showSuccess('Statistics loaded successfully');
    } catch (error) {
      console.error('Error fetching migration history:', error);
      const errorMsg = error?.response?.data || error.message || 'Failed to load statistics';
      Notification.showError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setStartDate('');
    setEndDate('');
    setStatistics(null);
  };

  const renderStatistics = () => {
    if (!statistics) return null;

    const dataTypes = Object.entries(statistics.statistics || {});
    const hasData = dataTypes.length > 0;

    return (
      <div className="ihub-stats-wrapper">
        <Card className="ihub-stat-card ihub-summary-card-mb">
          <CardContent>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
              <div>
                <Typography variant="h6" gutterBottom>
                  Migration Summary
                </Typography>
                <Typography variant="body2" className="ihub-summary-text-09">
                  Customer: <strong>{statistics.customerNumber}</strong>
                </Typography>
                {statistics.dateRange && (
                  <Typography variant="caption" className="ihub-summary-text-08">
                    {statistics.dateRange.start
                      ? new Date(statistics.dateRange.start).toLocaleDateString()
                      : 'All time'}{' '}
                    -{' '}
                    {statistics.dateRange.end
                      ? new Date(statistics.dateRange.end).toLocaleDateString()
                      : 'Now'}
                  </Typography>
                )}
              </div>
              <div className="ihub-summary-right">
                <Typography variant="h3" fontWeight="bold">
                  {statistics.totalRecords || 0}
                </Typography>
                <Typography variant="body2">Total Records</Typography>
              </div>
            </Stack>
          </CardContent>
        </Card>

        {hasData ? (
          <Grid container spacing={2}>
            {dataTypes.map(([dataType, count]) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={dataType}>
                <Card className="ihub-data-type-card" variant="outlined">
                  <CardContent>
                    <Stack spacing={2}>
                      <div className="ihub-flex-between">
                        <div style={{ color: getDataTypeColor(dataType, theme.tokens) }}>
                          {getDataTypeIcon(dataType)}
                        </div>
                        <Chip
                          label={count}
                          color="primary"
                          className="ihub-data-type-chip"
                          style={{ backgroundColor: getDataTypeColor(dataType, theme.tokens) }}
                        />
                      </div>
                      <div>
                        <Typography variant="h6" gutterBottom>
                          {dataType}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {((count / statistics.totalRecords) * 100).toFixed(1)}% of total
                        </Typography>
                      </div>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Alert severity="info">No migration records found for the selected criteria</Alert>
        )}
      </div>
    );
  };

  return (
    <Card variant="outlined" className="ihub-result-card">
      <CardContent>
        <div className="ihub-header-mb">
          <Typography variant="h5" gutterBottom fontWeight="bold">
            Migration History
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View migration statistics by customer and date range
          </Typography>
        </div>

        <Paper variant="outlined" className="ihub-search-paper">
          <Grid container spacing={2} alignItems="flex-end">
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Customer</InputLabel>
                <Select
                  value={selectedCustomer}
                  onChange={e => setSelectedCustomer(e.target.value)}
                  label="Customer"
                  disabled={loadingCustomers || loading}
                >
                  {loadingCustomers ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} className="ihub-loading-mr" />
                      Loading customers...
                    </MenuItem>
                  ) : customers.length === 0 ? (
                    <MenuItem disabled>No customers available</MenuItem>
                  ) : (
                    customers.map(customer => (
                      <MenuItem key={customer.customerNumber} value={customer.customerNumber}>
                        {getCustomerDisplayName(customer.customerNumber, customer.name)}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="Start Date"
                type="datetime-local"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={loading}
                error={isInvalidRange}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="End Date"
                type="datetime-local"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={loading}
                error={isInvalidRange}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 2 }}>
              <Stack direction="row" spacing={1}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSearch}
                  disabled={!selectedCustomer || loading || isInvalidRange}
                  startIcon={loading ? <CircularProgress size={16} /> : <SearchIcon />}
                >
                  {loading ? 'Loading...' : 'Search'}
                </Button>
                <Button variant="outlined" onClick={handleClear} disabled={loading}>
                  Clear
                </Button>
              </Stack>
            </Grid>
          </Grid>
          {isInvalidRange && (startDate || endDate) && (
            <div className="ihub-error-mt">
              <Alert severity="error" variant="outlined">
                End date cannot be before start date.
              </Alert>
            </div>
          )}
        </Paper>

        {loading && (
          <div className="ihub-loading-center">
            <CircularProgress />
          </div>
        )}

        {renderStatistics()}

        {!statistics && !loading && (
          <div className="ihub-empty-state">
            <AssessmentIcon className="ihub-empty-icon" />
            <Typography variant="h6" gutterBottom>
              Select a customer to view migration statistics
            </Typography>
            <Typography variant="body2">
              Use the date filters to narrow down results to a specific time period
            </Typography>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

CustomerMigrationHistory.propTypes = {
  initialCustomer: PropTypes.string,
};

export default CustomerMigrationHistory;
